/**
 * PinnedSection (Iter236 Phase 2)
 *
 * Renders the "Pinned" bar at the top of the Sidebar. Consumes
 * `GET /api/nav-pins` on mount, and re-fetches whenever the active
 * workspace changes (pins are scoped by user+workspace on the
 * backend).
 *
 * Callers pass:
 *   - `visibleLeaves` — the flat list of visible nav leaves (from
 *     `buildSidebarWithGroups`, group_header entries stripped) so we
 *     can resolve each `pin_id` (testId) back to its route + label.
 *   - `iconMap` — string → lucide component map (shared with Sidebar).
 *   - `pinnedIds` / `setPinnedIds` — controlled from Sidebar so the
 *     pin-toggle on each leaf and the section render both stay in
 *     sync without a second network round-trip.
 */
import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Pin } from 'lucide-react';
import { cn } from '../../lib/utils';

export function PinnedSection({
  visibleLeaves,
  iconMap,
  pinnedIds,
  onUnpin,
}) {
  const location = useLocation();
  if (!pinnedIds || pinnedIds.length === 0) return null;

  // Resolve pin ids in submitted order, filtering out any that the
  // caller can no longer see (e.g. workspace changed / role changed).
  const byTestId = new Map(visibleLeaves.map((it) => [it.testId, it]));
  const resolved = pinnedIds
    .map((id) => byTestId.get(id))
    .filter(Boolean);
  if (resolved.length === 0) return null;

  return (
    <div className="px-1" data-testid="sidebar-pinned-section">
      <div
        className="px-2 pt-1 pb-1 text-[10px] font-semibold uppercase tracking-wide text-amber-700 flex items-center gap-2"
        data-testid="sidebar-pinned-header"
      >
        <Pin className="w-3 h-3 flex-shrink-0" fill="currentColor" />
        <span className="sidebar-text">Pinned</span>
      </div>
      <div className="space-y-1">
        {resolved.map((item) => {
          const Icon = iconMap[item.iconKey];
          const isActive = location.pathname === item.href
            || (item.href !== '/dashboard'
                && location.pathname.startsWith(item.href));
          return (
            <div
              key={`pinned-${item.testId}`}
              className="group relative"
              data-testid={`pinned-row-${item.testId}`}
            >
              <NavLink
                to={item.href}
                data-testid={`pinned-${item.testId}`}
                className={cn(
                  'sidebar-link pr-8',
                  isActive && 'sidebar-link-active',
                )}
              >
                {Icon ? <Icon className="w-4 h-4 flex-shrink-0" /> : null}
                <span className="sidebar-text truncate">{item.name}</span>
              </NavLink>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onUnpin(item.testId);
                }}
                data-testid={`unpin-${item.testId}`}
                title="Unpin"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-muted opacity-60 hover:opacity-100 transition-opacity"
              >
                <Pin
                  className="w-3.5 h-3.5 text-amber-600"
                  fill="currentColor"
                />
              </button>
            </div>
          );
        })}
      </div>
      <div className="mx-2 my-2 border-b border-border/60" />
    </div>
  );
}
